/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
import { useState, useEffect, useCallback } from 'react'
import { useRecoilValue } from 'recoil'
import { fetchDataSelector } from '../../../../../components/recoil/selectors/selectors'
import utils from '../../../../../../utils/utils'
import { ManualReviewAPIEndpoints } from '@pages/manualReviewTab/api'

/**
 * @description Custom hook to manage data fetching and manipulation for the manual review events grid.
 * @param {object} props - The props for the hook.
 * @param {object} props.gridApi - The AG-Grid API.
 * @param {object} props.gridRef - The ref for the AG-Grid component.
 * @param {object} props.observationDetails - The details of the current observation.
 * @returns {object} An object containing perPageSize, onChangeHandler, and onRefreshClickHandler.
 */
const useManualReviewEventsData = ({
  gridApi,
  gridRef,
  observationDetails,
}) => {
  const initialState = {
    perPageSize: 100,
  }

  const getCoreData = useRecoilValue(fetchDataSelector)
  const [perPageSize, setPerPageSize] = useState(initialState.perPageSize)

  /**
   * @description Handles the page size change event from the grid.
   * @param {object} event - The event object.
   * @param {object} params - The parameters from the event.
   */
  const onChangeHandler = (event, params) => {
    const ddlValue = params?.value
    const newSize = parseInt(ddlValue)
    setPerPageSize(newSize)

    if (gridRef.current.api) {
      gridRef.current.api.paginationGoToPage(0)
      gridApi.rowModel.cacheParams.blockSize = newSize
      gridApi.paginationSetPageSize(newSize)
      gridApi.purgeInfiniteCache()
    }
  }

  /**
   * @description Handles the refresh button click event to reset and refresh the grid.
   */
  const onRefreshClickHandler = () => {
    if (!gridApi) return

    const sortModel = gridApi.rowModel?.cacheParams?.sortModel
    const filterModel = gridApi.rowModel?.cacheParams?.filterModel

    let refresh = false
    if (sortModel?.length > 0) {
      gridApi.columnModel?.resetColumnState()
      refresh = true
    }
    if (Object.keys(filterModel)?.length > 0) {
      gridApi.setFilterModel(null)
      refresh = true
    }
    if (!refresh) {
      gridApi.refreshInfiniteCache()
    } else {
      // Refresh the data cache to fetch latest data from API
      gridApi.purgeInfiniteCache()
    }
  }

  /**
   * @description Fetches the list of event log data for the grid.
   */
  /* istanbul ignore next */
  const getListOfEventLogData = useCallback(() => {
    const dataSource = {
      getRows: (params) => {
        const { startRow, endRow } = params
        gridApi.showLoadingOverlay()
        // Calculate page number and page size for the API
        const pageSize = endRow - startRow
        const pageNumber = params?.endRow / pageSize

        const postData = {
          pageNumber: pageNumber,
          pageSize: pageSize,
          observationId: observationDetails?.id,
        }

        getCoreData(
          ManualReviewAPIEndpoints?.getAllEventStatus?.method,
          ManualReviewAPIEndpoints?.getAllEventStatus?.url,
          postData,
          null,
          null,
          true,
          utils?.apiDomainName?.privateLand?.name
        )
          .then((response) => {
            if (response?.status === utils?.apiResponseCode?.successCode) {
              params.successCallback(
                response?.data?.data,
                response?.data?.totalCount
              )
              if (response?.data?.data?.length > 0) {
                gridApi.hideOverlay()
              } else {
                gridApi.showNoRowsOverlay()
              }
            } else {
              params.successCallback([], 0)
              gridApi.showNoRowsOverlay()
            }
          })
          .catch(() => {
            params.successCallback([], 0)
            gridApi.showNoRowsOverlay()
          })
      },
    }
    if (gridApi) {
      gridApi.setDatasource(dataSource)
    }
  }, [gridApi, getCoreData, observationDetails])

  useEffect(() => {
    if (gridApi) {
      getListOfEventLogData()
    }
  }, [gridApi, getListOfEventLogData])

  return {
    perPageSize,
    onChangeHandler,
    onRefreshClickHandler,
  }
}

export default useManualReviewEventsData
